/**
 * QualidadeDosDados — onde o registro de invocações está incompleto e se o custo
 * espelhado bate com a Wallet (GET /observability/data-quality). A aba inteira é
 * reconciliação de custo: exige `budget_costs:view` (403 → "sem permissão para ver custos").
 */
import { C } from '@/theme/palette';
import { LoadingSpinner, QueryError } from '@/components/ui/QueryState';
import { useObsQualidade } from '@/api/observability';
import { SEM_PERMISSAO_DE_CUSTO, custo, diaLongo, idCurto, inteiro, pct } from './formato';
import { Nota, Titulo, Vazio, estilos } from './partes';

function Indicador({ titulo, valor, detalhe, explicacao, alerta = false, testId }) {
  return (
    <div data-testid={testId} style={{
      flex: '1 1 260px', minWidth: 0, background: C.blackElev, borderRadius: 12, padding: '14px 16px',
      border: `1px solid ${alerta ? `${C.warn}66` : C.blackBorder}`,
    }}>
      <div style={{ fontSize: 12, letterSpacing: '.04em', textTransform: 'uppercase', color: C.neutralMuted }}>{titulo}</div>
      <div style={{ fontSize: 24, fontWeight: 700, marginTop: 6, color: alerta ? C.warn : C.text, fontVariantNumeric: 'tabular-nums' }}>
        {valor}
      </div>
      {detalhe ? <div style={{ fontSize: 12.5, color: C.neutralDim, marginTop: 2 }}>{detalhe}</div> : null}
      <p style={{ fontSize: 12.5, color: C.neutralMuted, lineHeight: 1.5, margin: '8px 0 0' }}>{explicacao}</p>
    </div>
  );
}

export function QualidadeDosDados({ periodo, aoAbrirExecucao }) {
  const s = estilos();
  const { data, isLoading, isError, error, refetch } = useObsQualidade(periodo);

  if (isLoading) return <LoadingSpinner label="Carregando a qualidade dos dados..." />;
  if (isError) {
    if (error?.isForbidden || error?.status === 403) {
      return (
        <div role="alert" data-testid="qualidade-sem-permissao" style={{
          background: `${C.warn}14`, border: `1px solid ${C.warn}44`, borderRadius: 10,
          padding: '14px 16px', color: C.neutralDim, fontSize: 14,
        }}>
          <strong style={{ color: C.warn }}>{SEM_PERMISSAO_DE_CUSTO}.</strong>{' '}
          Esta aba é a reconciliação do custo com a Wallet e exige a permissão{' '}
          <code>budget_costs:view</code>. Peça acesso ao Administrador do tenant.
        </div>
      );
    }
    return <QueryError error={error} onRetry={refetch} />;
  }

  const q = data || {};
  const total = Number(q.invocacoes || 0);
  const r = q.reconciliacao || {};
  const semWallet = q.com_custo_e_sem_wallet || {};
  const orfas = r.invocacoes_sem_linha_na_wallet || {};
  const soWallet = r.wallet_sem_invocacao || {};
  const parte = (n) => (total > 0 ? `${pct((Number(n || 0) / total) * 100)} das invocações` : null);
  const divergencias = r.divergencias || [];

  return (
    <div data-testid="obs-qualidade">
      <div style={{ color: C.neutralDim, fontSize: 13, marginBottom: 12 }}>
        Período: {diaLongo(q.periodo?.de)} a {diaLongo(q.periodo?.ate)} (UTC) ·{' '}
        <strong style={{ color: C.text }}>{inteiro(total)}</strong> invocação(ões) registradas, incluindo as de execuções excluídas.
      </div>

      {total === 0 ? (
        <Vazio>Nenhuma invocação registrada no período — não há o que conferir.</Vazio>
      ) : null}

      <Titulo>Lacunas do registro</Titulo>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <Indicador testId="dq-sem-agente" titulo="Sem agente" valor={inteiro(q.sem_agente)} detalhe={parte(q.sem_agente)}
          alerta={q.sem_agente > 0}
          explicacao="Invocações que não foram atribuídas a nenhum agente do squad. O custo existe, mas não entra no ranking de ninguém — quanto maior, menos confiável a comparação entre agentes." />
        <Indicador testId="dq-sem-custo" titulo="Sem custo" valor={inteiro(q.sem_custo)} detalhe={parte(q.sem_custo)}
          alerta={q.sem_custo > 0}
          explicacao="Invocações concluídas (OK ou sem mudança) com custo zero, fora as delegações. Indica custo que não foi registrado: o total em US$ está subestimado." />
        <Indicador testId="dq-sem-wallet" titulo="Com custo e sem vínculo com a Wallet" valor={inteiro(semWallet.invocacoes)}
          detalhe={custo(semWallet.custo_usd)} alerta={semWallet.invocacoes > 0}
          explicacao="Invocações que têm custo mas não guardaram o identificador da transação na Wallet. Esse valor não pode ser conferido contra a fonte." />
        <Indicador testId="dq-sem-duracao" titulo="Sem duração" valor={inteiro(q.sem_duracao)} detalhe={parte(q.sem_duracao)}
          alerta={q.sem_duracao > 0}
          explicacao="Invocações sem tempo medido, fora as delegações. Ficam de fora da mediana e do p90 de duração." />
        <Indicador testId="dq-sem-execucao" titulo="Fora de execução" valor={inteiro(q.sem_execucao)} detalhe={parte(q.sem_execucao)}
          explicacao="Invocações feitas fora de uma execução (wizard, Design Studio). Não é defeito: mostra quanto do uso de IA acontece antes de executar." />
      </div>

      <Titulo>Reconciliação com a Wallet</Titulo>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <Indicador testId="dq-pares" titulo="Pares conferidos" valor={inteiro(r.pares)}
          detalhe={`invocações ${custo(r.custo_das_invocacoes_usd)} × Wallet ${custo(r.custo_da_wallet_usd)}`}
          explicacao="Invocações que têm a linha correspondente na Wallet (mesma transação). São os únicos valores que dá para comparar um a um." />
        <Indicador testId="dq-diferenca" titulo="Diferença de custo" valor={custo(r.diferenca_usd)}
          detalhe="invocações − Wallet, nos pares" alerta={Math.abs(Number(r.diferenca_usd || 0)) > Number(r.tolerancia_usd || 0)}
          explicacao="Soma do custo das invocações menos a soma da Wallet nos pares conferidos. A Wallet é a fonte: diferença indica espelho desatualizado ou gravado errado." />
        <Indicador testId="dq-divergentes" titulo="Pares divergentes" valor={inteiro(r.divergentes)}
          detalhe={`tolerância de US$ ${Number(r.tolerancia_usd || 0).toLocaleString('pt-BR', { maximumFractionDigits: 6 })} por par`}
          alerta={r.divergentes > 0}
          explicacao="Pares em que o custo da invocação e o da Wallet diferem acima da tolerância. Estão listados abaixo." />
        <Indicador testId="dq-orfas" titulo="Invocações sem linha na Wallet" valor={inteiro(orfas.invocacoes)}
          detalhe={custo(orfas.custo_usd)} alerta={orfas.invocacoes > 0}
          explicacao="Invocações que apontam para uma transação que não existe na Wallet. Custo visto aqui e ausente do orçamento e da tela de Custos." />
        <Indicador testId="dq-so-wallet" titulo="Wallet sem invocação" valor={inteiro(soWallet.linhas)}
          detalhe={custo(soWallet.custo_usd)} alerta={soWallet.linhas > 0}
          explicacao="Linhas de chat/codegen da Wallet sem invocação correspondente. É custo real que esta tela não atribui a nenhum agente." />
      </div>

      <Titulo lado={`${inteiro(r.divergentes)} divergência(s) · ${inteiro(r.divergencias_detalhadas ?? divergencias.length)} detalhada(s)`}>
        Divergências
      </Titulo>
      {divergencias.length === 0 ? (
        <Vazio>Nenhum par divergente no período.</Vazio>
      ) : (
        <div style={{ overflowX: 'auto' }} data-testid="dq-divergencias">
          <table style={s.tabela}>
            <thead>
              <tr>
                <th style={s.th}>Agente</th>
                <th style={s.th}>Passo</th>
                <th style={s.th}>Execução</th>
                <th style={s.thNum}>Invocação</th>
                <th style={s.thNum}>Wallet</th>
                <th style={s.thNum}>Diferença</th>
                <th style={s.th}>Transação</th>
              </tr>
            </thead>
            <tbody>
              {divergencias.map((d) => (
                <tr key={d.wallet_txn_id}>
                  <td style={{ ...s.td, wordBreak: 'break-word' }}>{d.agent_name}</td>
                  <td style={{ ...s.td, wordBreak: 'break-word' }}>{d.passo}</td>
                  <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
                    {d.execution_id ? (
                      <button type="button" style={s.link} title={d.execution_id} onClick={() => aoAbrirExecucao(d.execution_id)}>
                        {idCurto(d.execution_id)}
                      </button>
                    ) : '—'}
                  </td>
                  <td style={s.tdNum}>{custo(d.custo_da_invocacao_usd)}</td>
                  <td style={s.tdNum}>{custo(d.custo_da_wallet_usd)}</td>
                  <td style={{ ...s.tdNum, color: C.warn }}>{custo(d.diferenca_usd)}</td>
                  <td style={{ ...s.td, wordBreak: 'break-all', color: C.neutralMuted, fontSize: 12 }}>{d.wallet_txn_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {Number(r.divergencias_cortadas || 0) > 0 ? (
        <Nota testId="dq-cortadas">
          A API detalha as {inteiro(r.divergencias_detalhadas)} maiores divergências;{' '}
          <strong>{inteiro(r.divergencias_cortadas)}</strong> outra(s) não aparecem nesta lista (o total acima conta todas).
        </Nota>
      ) : null}
    </div>
  );
}

export default QualidadeDosDados;
